import { useEffect, useRef } from "react";
import { Coordinate } from "ol/coordinate";
import Draw, { DrawEvent } from "ol/interaction/Draw";
import DoubleClickZoom from "ol/interaction/DoubleClickZoom";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Fill from "ol/style/Fill";
import Stroke from "ol/style/Stroke";
import Style from "ol/style/Style";
import { ANNOTATION_COLOR } from "../constants/color";
import { useMap } from "../hooks";
import {
  FINISH_BY_CONDITION_MIN_POINTS,
  confirmedSketchPointCount,
} from "../utils/drawSketch";
import { positionsFromFeature } from "../../utils";

/**
 * headless 폴리곤 드로잉 인터랙션 — **마운트=활성, 언마운트=완전 정리**.
 *
 * `DrawingTools`(툴바형)와 달리 DOM 을 렌더하지 않고 버튼/ControlSection 없이
 * 조건부 렌더만으로 그리기를 시작·종료한다. 화면 상태(예: "그리기 모드")가
 * 그리기 활성화의 단일 SoT 일 때 쓴다. `useMap` 컨텍스트 필요(MapContainer 내부 전용).
 *
 * - 더블클릭 = 완성(`onDrawEnd`) — 꼭짓점이 3개 미만이면 완료하지 않고 스케치를 이어 간다
 *   (2D·3D 스펙 「3점 미만 완료 불가」, 3D `Viewer.PolygonDrawInteraction` 과 같다). ESC/우클릭 = 현재 스케치 취소(`onAbort`)
 * - 그리는 동안 DoubleClickZoom 비활성·crosshair 커서, 정리 시 원상 복원
 * - 완성된 스케치는 콜백 후 자체 제거한다 — 결과 표시는 호출부가
 *   `CustomPolygon` 등으로 소유한다
 */
export interface PolygonDrawInteractionProps {
  /** 스케치 스타일 색상 (`ANNOTATION_COLOR` 키) */
  color?: keyof typeof ANNOTATION_COLOR;
  /** 폴리곤 완성 시 WGS84 `[lng, lat]` 외곽 링 전달 (OL 관례상 첫 점=끝 점 닫힌 링) */
  onDrawEnd: (ring: Coordinate[]) => void;
  /** ESC/우클릭으로 스케치를 취소했을 때 — 호출부가 모드 종료 등 상태 전이를 수행 */
  onAbort?: () => void;
}

/** 2D·3D 스펙 공통 — 꼭짓점이 이보다 적으면 완료(더블클릭·재클릭)를 무시하고 그리기를 잇는다 */
const MIN_POLYGON_VERTICES = 3;
const SKETCH_STYLE_Z_INDEX = 1000;
const SKETCH_STROKE_WIDTH = 2;
const SKETCH_FILL_OPACITY = 0.2;

export function PolygonDrawInteraction({
  color = "BLUE",
  onDrawEnd,
  onAbort,
}: PolygonDrawInteractionProps) {
  const map = useMap();
  // 콜백 최신값 ref — 콜백 identity 변화로 인터랙션이 재설치되지 않게 한다.
  // 렌더 중 ref 쓰기는 React Compiler 최적화를 막으므로 커밋 후(effect) 동기화한다.
  const onDrawEndRef = useRef(onDrawEnd);
  const onAbortRef = useRef(onAbort);
  useEffect(() => {
    onDrawEndRef.current = onDrawEnd;
    onAbortRef.current = onAbort;
  });

  useEffect(() => {
    const sketchSource = new VectorSource();
    const sketchLayer = new VectorLayer({ source: sketchSource });
    map.addLayer(sketchLayer);

    // 완료 판정은 래퍼가 한다 — 꼭짓점이 모자라면 OL 의 재클릭 완료도 더블클릭도 완료하지 않는다
    const hasEnoughVertices = (): boolean =>
      confirmedSketchPointCount(draw, "polygon") >= MIN_POLYGON_VERTICES;
    const draw: Draw = new Draw({
      type: "Polygon",
      source: sketchSource,
      minPoints: FINISH_BY_CONDITION_MIN_POINTS,
      finishCondition: hasEnoughVertices,
      style: new Style({
        zIndex: SKETCH_STYLE_Z_INDEX,
        stroke: new Stroke({
          color: ANNOTATION_COLOR[color].stroke(1),
          width: SKETCH_STROKE_WIDTH,
        }),
        fill: new Fill({
          color: ANNOTATION_COLOR[color].fill(SKETCH_FILL_OPACITY),
        }),
      }),
    });

    const handleDrawEnd = (event: DrawEvent) => {
      const rings = positionsFromFeature(event.feature, true) as Coordinate[][];
      onDrawEndRef.current(rings[0] ?? []);
      // OL 은 drawend 디스패치 직후 완성 피처를 source 에 넣는다 —
      // 마이크로태스크로 비워 결과 표시 소유권을 호출부에 넘긴다(잔여 스케치 방지)
      queueMicrotask(() => sketchSource.clear());
    };
    draw.on("drawend", handleDrawEnd);
    map.addInteraction(draw);

    // 더블클릭=완성이 줌과 충돌하지 않게 그리는 동안 DoubleClickZoom 비활성
    map.getInteractions().forEach((interaction) => {
      if (interaction instanceof DoubleClickZoom) interaction.setActive(false);
    });
    map.setProperties({ isDrawing: true });

    const viewport = map.getViewport();
    const previousCursor = viewport.style.cursor;
    viewport.style.cursor = "crosshair";

    const abortSketch = () => {
      draw.abortDrawing();
      sketchSource.clear();
      onAbortRef.current?.();
    };
    const handleContextMenu = (event: MouseEvent) => {
      event.preventDefault();
      abortSketch();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") abortSketch();
    };
    // 더블클릭의 첫 클릭이 마지막 꼭짓점을 확정한다 — 모자라면 스케치를 그대로 두고 그리기를 잇는다
    const handleDoubleClick = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (!hasEnoughVertices()) return;
      draw.finishDrawing();
    };
    viewport.addEventListener("contextmenu", handleContextMenu);
    viewport.addEventListener("dblclick", handleDoubleClick, { capture: true });
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      viewport.removeEventListener("contextmenu", handleContextMenu);
      viewport.removeEventListener("dblclick", handleDoubleClick, {
        capture: true,
      });
      document.removeEventListener("keydown", handleKeyDown);
      draw.un("drawend", handleDrawEnd);
      map.removeInteraction(draw);
      map.removeLayer(sketchLayer);
      sketchSource.clear();
      map.getInteractions().forEach((interaction) => {
        if (interaction instanceof DoubleClickZoom) interaction.setActive(true);
      });
      map.setProperties({ isDrawing: false });
      viewport.style.cursor = previousCursor;
    };
  }, [map, color]);

  return null;
}
